// =============================================================================
// SimSense Platform — Setup Viewer & Diff Component
// =============================================================================
// Handles rendering of iRacing .sto setup data with support for
// side-by-side comparison, unit conversion, and categorized grid layout.
// =============================================================================

import { useState } from "react";
import { useUnits } from "../hooks/useUnits";

const IRRELEVANT_METADATA = ["UpdateCount", "Name", "version", "carName", "Update count", "SteeringRatio"];

const humanizeKey = (key: string): string => {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, s => s.toUpperCase())
    .replace(/\b(Ls|Hs)\b/g, s => s.toUpperCase()) // LS/HS shock prefixes
    .replace(/\bArb\b/g, "ARB") // Anti-roll bar
    .trim();
};

export const flattenSetup = (obj: any, prefix = ""): Record<string, any> => {
  const result: Record<string, any> = {};
  if (!obj || typeof obj !== "object") return result;
  Object.entries(obj).forEach(([key, value]) => {
    if (IRRELEVANT_METADATA.includes(key)) return;
    const fullKey = prefix ? `${prefix}_${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(result, flattenSetup(value, fullKey));
    } else {
      result[fullKey] = value;
    }
  });
  return result;
};

const convertSetupString = (str: string, system: string, key?: string): string => {
  if (/\d+C\b/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)C\b/g, (_, n) => `${Math.round(parseFloat(n) * 9 / 5 + 32)}F`);
  }
  if (/\d+F\b/.test(str) && system === "metric") {
    return str.replace(/(\d+(?:\.\d+)?)F\b/g, (_, n) => `${Math.round((parseFloat(n) - 32) * 5 / 9)}C`);
  }
  if (/\d+\s*kPa/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*kPa/g, (_, n) => {
      const psi = parseFloat(n) * 0.145038;
      // OT-192: Cold pressures snap to 0.5 psi increments
      const lk = (key || "").toLowerCase();
      if (lk.includes("coldpress") || lk.includes("cold")) {
        const snapped = Math.round(psi * 2) / 2;
        return `${snapped % 1 === 0 ? snapped.toFixed(1) : snapped.toFixed(1)} psi`;
      }
      return `${psi.toFixed(1)} psi`;
    });
  }
  // OT-176: N/mm → lbs/in, snapped to nearest 25 lbs/in (iRacing garage increment)
  if (/\d+\s*N\/mm/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\/mm/g, (_, n) => {
      const lbsIn = parseFloat(n) * 5.71015;
      const snapped = Math.round(lbsIn / 25) * 25;
      return `${snapped} lbs/in`;
    });
  }
  if (/\d+(?:\.\d+)?\s*Nm/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*Nm/g, (_, n) => `${(parseFloat(n) * 0.737562).toFixed(1)} ft-lbs`);
  }
  if (/\d+\s*N\b/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\b/g, (_, n) => `${Math.round(parseFloat(n) * 0.224809)} lbs`);
  }
  if (/\d+\s*N\b/.test(str) && system === "metric") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\b/g, (_, n) => `${Math.round(parseFloat(n) / 9.80665)} kg`);
  }
  // OT-177/178/171: mm → in with key-aware rounding
  if (/[+-]?\d+(?:\.\d+)?\s*mm/.test(str) && system === "standard") {
    return str.replace(/([+-]?\d+(?:\.\d+)?)\s*mm/g, (_, n) => {
      const inches = parseFloat(n) / 25.4;
      const lk = (key || "").toLowerCase();
      // OT-177: Ballast snaps to 0.5" increments
      if (lk.includes("ballast")) {
        const snapped = Math.round(inches * 2) / 2;
        return `${snapped < 0 ? "" : "+"}${snapped.toFixed(1)} in`;
      }
      // OT-178: Link slack as unsigned x/16"
      if (lk.includes("slack") || lk.includes("link")) {
        const sixteenths = Math.round(Math.abs(inches) * 16);
        return `${sixteenths}/16"`;
      }
      // OT-171: Toe-in as signed x/32"
      if (lk.includes("toe")) {
        const thirtySeconds = Math.round(inches * 32);
        return `${thirtySeconds >= 0 ? "+" : ""}${thirtySeconds}/32"`;
      }
      // OT-187: ARB diameter snaps to 1/16" increments (2.500, 2.438, 2.375...)
      if (lk.includes("diameter")) {
        const snapped = Math.round(inches * 16) / 16;
        return `${snapped.toFixed(3)} in`;
      }
      // Trackbar snaps to 0.250" increments
      if (lk.includes("trackbar")) {
        const snapped = Math.round(inches / 0.25) * 0.25;
        return `${snapped >= 0 ? "+" : ""}${snapped.toFixed(3)} in`;
      }
      return `${inches.toFixed(3)} in`;
    });
  }
  return str;
};

export const renderSetupValue = (key: string, val: any, units: any): string => {
  if (val === null || val === undefined) return "—";
  const system = units?.system || "standard";
  if (typeof val === "string") return convertSetupString(val, system, key);
  const upperKey = key.toUpperCase();
  if (typeof val === "number") {
    // OT-188: Steering ratio — show raw value until per-car mapping is verified
    if (upperKey.includes("STEERINGRATIO")) return String(val);
    // Boolean-like 0/1 values → checkmark display
    if ((val === 0 || val === 1) && (upperKey.includes("ATTACH") || upperKey.includes("LOCKED") || upperKey.includes("ENABLED"))) {
      return val === 1 ? "☑" : "☐";
    }
    if (upperKey.includes("TEMP")) return units?.formatTemp?.(val) ?? String(val);
    if (upperKey.includes("PRESS")) return units?.formatPressure?.(val) ?? String(val);
    if (upperKey.includes("WEIGHT") || upperKey.includes("RATE")) return units?.formatWeight?.(val) ?? String(val);
    return String(val);
  }
  if (typeof val === "object" && !Array.isArray(val)) {
    return Object.entries(val).map(([k, v]) => `${k}: ${typeof v === "object" ? "{...}" : v}`).join(", ");
  }
  return String(val);
};

interface SetupItem { flatKey: string; displayKey: string; value: any }
interface SetupPair {
  label: string;
  left: { label: string; items: SetupItem[] };
  right: { label: string; items: SetupItem[] };
}
interface SetupSectionData { name: string; pairs: SetupPair[]; shared: SetupItem[] }

const POSITION_KEYS = ["LeftFront", "RightFront", "LeftRear", "RightRear"];

export const buildSetupSections = (setup: any): SetupSectionData[] => {
  if (!setup || typeof setup !== "object") return [];
  const sections: SetupSectionData[] = [];
  const generalItems: SetupItem[] = [];

  const flattenSub = (obj: any, prefix: string): SetupItem[] => {
    if (!obj || typeof obj !== "object") return [];
    const items: SetupItem[] = [];
    for (const [k, v] of Object.entries(obj)) {
      if (IRRELEVANT_METADATA.includes(k)) continue;
      const flatKey = `${prefix}_${k}`;
      if (typeof v === "object" && v !== null && !Array.isArray(v)) {
        for (const [sk, sv] of Object.entries(v as Record<string, unknown>)) {
          items.push({ flatKey: `${flatKey}_${sk}`, displayKey: humanizeKey(sk), value: sv });
        }
      } else {
        items.push({ flatKey, displayKey: humanizeKey(k), value: v });
      }
    }
    return items;
  };

  for (const [topKey, topVal] of Object.entries(setup)) {
    if (IRRELEVANT_METADATA.includes(topKey)) continue;
    if (typeof topVal !== "object" || topVal === null || Array.isArray(topVal)) {
      generalItems.push({ flatKey: topKey, displayKey: humanizeKey(topKey), value: topVal });
      continue;
    }
    const sub = topVal as Record<string, any>;
    const subKeys = Object.keys(sub);
    const hasPositions = subKeys.some(k => POSITION_KEYS.includes(k));
    const section: SetupSectionData = { name: humanizeKey(topKey), pairs: [], shared: [] };

    if (hasPositions) {
      if (sub.LeftFront || sub.RightFront) {
        section.pairs.push({
          label: "Front",
          left: { label: "LF", items: flattenSub(sub.LeftFront, `${topKey}_LeftFront`) },
          right: { label: "RF", items: flattenSub(sub.RightFront, `${topKey}_RightFront`) },
        });
      }
      if (sub.LeftRear || sub.RightRear) {
        section.pairs.push({
          label: "Rear",
          left: { label: "LR", items: flattenSub(sub.LeftRear, `${topKey}_LeftRear`) },
          right: { label: "RR", items: flattenSub(sub.RightRear, `${topKey}_RightRear`) },
        });
      }
      for (const [subKey, subVal] of Object.entries(sub)) {
        if (POSITION_KEYS.includes(subKey)) continue;
        if (typeof subVal === "object" && subVal !== null && !Array.isArray(subVal)) {
          for (const [k, v] of Object.entries(subVal as Record<string, unknown>)) {
            if (IRRELEVANT_METADATA.includes(k)) continue;
            section.shared.push({
              flatKey: `${topKey}_${subKey}_${k}`,
              displayKey: humanizeKey(k as string),
              value: v,
            });
          }
        } else {
          section.shared.push({ flatKey: `${topKey}_${subKey}`, displayKey: humanizeKey(subKey), value: subVal });
        }
      }
    } else {
      for (const [k, v] of Object.entries(sub)) {
        if (IRRELEVANT_METADATA.includes(k)) continue;
        if (typeof v === "object" && v !== null && !Array.isArray(v)) {
          for (const [sk, sv] of Object.entries(v as Record<string, unknown>)) {
            section.shared.push({
              flatKey: `${topKey}_${k}_${sk}`,
              displayKey: `${humanizeKey(k)}: ${humanizeKey(sk as string)}`,
              value: sv,
            });
          }
        } else {
          section.shared.push({ flatKey: `${topKey}_${k}`, displayKey: humanizeKey(k), value: v });
        }
      }
    }
    if (section.pairs.length > 0 || section.shared.length > 0) sections.push(section);
  }
  if (generalItems.length > 0) {
    sections.push({ name: "General", pairs: [], shared: generalItems });
  }
  
  // Custom sorting / splitting for "Chassis"
  const tireIdx = sections.findIndex(s => s.name.toLowerCase().includes("tire"));
  const chassisIdx = sections.findIndex(s => s.name.toLowerCase().includes("chassis"));
  if (tireIdx >= 0 && chassisIdx >= 0) {
    const chassis = sections[chassisIdx];
    if (chassis.shared.length > 0 && chassis.pairs.length > 0) {
      const frontItems: SetupItem[] = [];
      const rearItems: SetupItem[] = [];
      for (const item of chassis.shared) {
        const lk = item.flatKey.toLowerCase();
        if (lk.includes("_rear_") && !lk.includes("frontarb")) {
          rearItems.push(item);
        } else {
          frontItems.push(item);
        }
      }
      chassis.shared = [];
      if (frontItems.length > 0) {
        sections.splice(tireIdx + 1, 0, { name: "Front End", pairs: [], shared: frontItems });
      }
      if (rearItems.length > 0) {
        sections.push({ name: "Rear Settings", pairs: [], shared: rearItems });
      }
    }
  }

  // === Custom Layout Post-Processing ===
  const CORNER_ORDER = [
    "corner weight", "ride height", "spring perch offset", "spring rate",
    "spring angle", "ls compression", "hs comp slope", "ls rebound",
    "hs rebound slope", "camber", "caster", "toe in",
  ];
  const cornerIdx = (dk: string) => {
    const lk = dk.toLowerCase();
    for (let i = 0; i < CORNER_ORDER.length; i++) {
      if (lk === CORNER_ORDER[i] || lk.endsWith(CORNER_ORDER[i])) return i;
    }
    return CORNER_ORDER.length;
  };

  // 1. Sort corner items & extract Truck Arm Preload
  const chassisSect = sections.find(s => s.name.toLowerCase().includes("chassis"));
  if (chassisSect) {
    for (const pair of chassisSect.pairs) {
      pair.left.items.sort((a, b) => cornerIdx(a.displayKey) - cornerIdx(b.displayKey));
      pair.right.items.sort((a, b) => cornerIdx(a.displayKey) - cornerIdx(b.displayKey));
    }
    const truckArm: SetupItem[] = [];
    for (const pair of chassisSect.pairs) {
      for (const side of [pair.left, pair.right]) {
        const extracted = side.items.filter(i => i.displayKey.toLowerCase().includes("truck arm preload"));
        truckArm.push(...extracted);
        side.items = side.items.filter(i => !i.displayKey.toLowerCase().includes("truck arm preload"));
      }
    }
    if (truckArm.length > 0) {
      chassisSect.shared = truckArm;
    }
  }

  // 2. Split Front End into Front / Front ARB pair
  const frontEndSect = sections.find(s => s.name === "Front End");
  if (frontEndSect && frontEndSect.shared.length > 0) {
    const FRONT_ORDER = [
      "ballast forward", "nose weight", "cross weight",
      "steering offset", "front brake bias", "tape configuration",
    ];
    const ARB_ORDER = ["diameter", "arm asymmetry", "link slack", "preload", "attach"];
    const frontItems: SetupItem[] = [];
    const arbItems: SetupItem[] = [];
    for (const item of frontEndSect.shared) {
      if (item.flatKey.toLowerCase().includes("frontarb") || item.flatKey.toLowerCase().includes("front_arb")) {
        arbItems.push(item);
      } else {
        frontItems.push(item);
      }
    }
    const sortByList = (items: SetupItem[], order: string[]) => {
      const idx = (dk: string) => {
        const lk = dk.toLowerCase();
        for (let i = 0; i < order.length; i++) {
          if (lk === order[i] || lk.endsWith(order[i]) || lk.includes(order[i])) return i;
        }
        return order.length;
      };
      items.sort((a, b) => idx(a.displayKey) - idx(b.displayKey));
    };
    sortByList(frontItems, FRONT_ORDER);
    sortByList(arbItems, ARB_ORDER);
    frontEndSect.shared = [];
    frontEndSect.pairs = [{
      label: "",
      left: { label: "Front", items: frontItems },
      right: { label: "Front ARB", items: arbItems },
    }];
  }

  // 3. Reorder Rear Settings
  const rearSect = sections.find(s => s.name === "Rear Settings");
  if (rearSect && rearSect.shared.length > 0) {
    const REAR_ORDER = ["rear end ratio", "arb diameter", "diameter", "preload", "attach"];
    const rIdx = (dk: string) => {
      const lk = dk.toLowerCase();
      for (let i = 0; i < REAR_ORDER.length; i++) {
        if (lk === REAR_ORDER[i] || lk.endsWith(REAR_ORDER[i]) || lk.includes(REAR_ORDER[i])) return i;
      }
      return REAR_ORDER.length;
    };
    rearSect.shared.sort((a, b) => rIdx(a.displayKey) - rIdx(b.displayKey));
  }

  return sections;
};

function SetupItemRow({ item, flatCompare, units }: { item: SetupItem; flatCompare: Record<string, any> | null; units: any }) {
  const compVal = flatCompare ? flatCompare[item.flatKey] : undefined;
  let diffColor = "inherit";
  let hasDiff = false;

  if (compVal !== undefined && item.value !== compVal) {
    hasDiff = true;
    if (typeof item.value === "number" && typeof compVal === "number") {
      if (item.value > compVal) diffColor = "var(--good)";
      else if (item.value < compVal) diffColor = "var(--bad)";
    } else {
      diffColor = "var(--accent)";
    }
  }

  const isAttach = item.flatKey.toLowerCase().includes("attach");
  const attachVal = isAttach && (item.value === 0 || item.value === 1 || item.value === "0" || item.value === "1");

  return (
    <div className={`setup-row ${hasDiff ? 'setup-row--changed' : ''}`}>
      <span>
        {item.displayKey}
      </span>
      <div className="setup-row-value">
        {attachVal ? (
          <span className="setup-row-value-attach" style={{ color: Number(item.value) === 1 ? "var(--good)" : "var(--text3)" }}>
            {Number(item.value) === 1 ? "\u2611" : "\u2610"}
          </span>
        ) : (
          <span className="setup-row-value-diff" style={{ color: hasDiff ? diffColor : "#fff" }}>
            {renderSetupValue(item.flatKey, item.value, units)}
          </span>
        )}
        {hasDiff && !attachVal && (
          <div className="setup-row-value-prev" style={{ color: diffColor }}>
            <span>prev:</span>
            {renderSetupValue(item.flatKey, compVal, units)}
          </div>
        )}
      </div>
    </div>
  );
}

export function SetupTab({ setup, compareSetup, compareLabel }: { setup: any; compareSetup?: any; compareLabel?: string }) {
  const units = useUnits();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  if (!setup || Object.keys(setup).length === 0) {
    return (
      <div className="no-setup-data">
        No setup data available for this session.
      </div>
    );
  }

  const sections = buildSetupSections(setup);
  const flatCompare = compareSetup ? flattenSetup(compareSetup) : null;

  const toggleSection = (name: string) => {
    setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="setup-tab">
      {compareSetup && (
        <div className="compare-info-bar">
          <div className="compare-info-bar-label">
            <span>Comparing against</span>
            {compareLabel || "Reference"}
          </div>
          <div className="compare-info-bar-legend">
            <span><span style={{ color: "var(--good)" }}>▲</span> Increase</span>
            <span><span style={{ color: "var(--bad)" }}>▼</span> Decrease</span>
            <span><span style={{ color: "var(--accent)" }}>•</span> Change</span>
          </div>
        </div>
      )}

      {/* Workbench toolbar */}
      <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "16px", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Search parameters..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: "150px", padding: "6px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "4px", color: "var(--text1)", fontSize: "12px" }}
        />
        {compareSetup && (
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text2)", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={showOnlyDiffs} onChange={e => setShowOnlyDiffs(e.target.checked)} style={{ accentColor: "var(--accent)" }} />
            Show only diffs
          </label>
        )}
        {searchQuery && (
          <span style={{ fontSize: "11px", color: "var(--text3)" }}>
            Filtering results...
          </span>
        )}
      </div>

      <div className="setup-sections-container">
        {sections.map(section => {
          const isCollapsed = collapsed[section.name] || false;
          const sq = searchQuery.toLowerCase();

          // Filter items based on search and diff-only mode
          const itemHasDiff = (item: SetupItem) => {
            if (!flatCompare) return false;
            const compVal = flatCompare[item.flatKey];
            if (compVal === undefined) return false;
            return renderSetupValue(item.flatKey, item.value, units) !== renderSetupValue(item.flatKey, compVal, units);
          };
          const itemMatchesSearch = (item: SetupItem) => !sq || item.displayKey.toLowerCase().includes(sq) || item.flatKey.toLowerCase().includes(sq);
          const shouldShowItem = (item: SetupItem) => {
            if (!itemMatchesSearch(item)) return false;
            if (showOnlyDiffs && flatCompare && !itemHasDiff(item)) return false;
            return true;
          };

          // Count visible items
          const visiblePairItems = section.pairs.reduce((sum, p) => sum + p.left.items.filter(shouldShowItem).length + p.right.items.filter(shouldShowItem).length, 0);
          const visibleSharedItems = section.shared.filter(shouldShowItem).length;
          const itemCount = visiblePairItems + visibleSharedItems;
          if (itemCount === 0 && (sq || showOnlyDiffs)) return null;
          
          return (
            <div key={section.name} className="setup-section">
              <button
                onClick={() => toggleSection(section.name)}
                className={`setup-section-header ${isCollapsed ? 'setup-section-header-collapsed' : ''}`}
              >
                <h3 className="setup-section-title">
                  {section.name}
                  <span className="setup-section-item-count">{itemCount}</span>
                </h3>
                <span className={`setup-section-toggle ${isCollapsed ? 'setup-section-toggle-collapsed' : ''}`}>▼</span>
              </button>
              
              {!isCollapsed && (
                <div className="setup-section-content">
                  {section.pairs.map((pair, pi) => (
                    <div key={pair.label} className="setup-pair" style={{ marginBottom: pi < section.pairs.length - 1 || section.shared.length > 0 ? "24px" : 0 }}>
                      <div className="setup-pair-title">
                        {pair.label}
                        <div />
                      </div>
                      
                      <div className="setup-pair-content">
                        {/* Left Side (LF/LR) */}
                        <div className="setup-side">
                          <div className="setup-side-title">{pair.left.label}</div>
                          {pair.left.items.filter(shouldShowItem).map(item => (
                            <SetupItemRow key={item.flatKey} item={item} flatCompare={flatCompare} units={units} />
                          ))}
                        </div>

                        {/* Right Side (RF/RR) */}
                        <div className="setup-side">
                          <div className="setup-side-title">{pair.right.label}</div>
                          {pair.right.items.filter(shouldShowItem).map(item => (
                            <SetupItemRow key={item.flatKey} item={item} flatCompare={flatCompare} units={units} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {section.shared.length > 0 && (
                    <div className="setup-shared" style={{ marginTop: section.pairs.length > 0 ? "24px" : 0 }}>
                      {section.pairs.length > 0 && (
                        <div className="setup-shared-title">
                          Shared
                          <div />
                        </div>
                      )}
                      <div className="setup-shared-content">
                        {section.shared.filter(shouldShowItem).map(item => (
                          <SetupItemRow key={item.flatKey} item={item} flatCompare={flatCompare} units={units} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

